import { useState } from 'react';
import { Lock, Shield, Key, Globe, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { useAppState } from '../hooks/useAppState';

const ADMIN_PASSWORD = 'flashcardgenerator-admin-2026';

export const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { settings, updateSettings } = useAppState();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid password');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle>Admin Dashboard</CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Enter password to access admin settings
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
              )}
              <Button type="submit" className="w-full" disabled={!password.trim()}>
                <Lock className="w-4 h-4 mr-2" />
                Unlock
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage AI providers and app settings
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout}>
          Lock
        </Button>
      </div>

      {/* AI Provider Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-blue-600" />
            <CardTitle>AI Provider Configuration</CardTitle>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Configure API keys for AI-powered flashcard generation
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Configure your AI provider settings. Currently supported providers:
          </p>

          <div className="grid gap-4">
            {[
              { id: 'pollination', name: 'Pollination AI', free: true, desc: 'Free, no API key required' },
              { id: 'openai', name: 'OpenAI (GPT-4)', free: false, desc: 'Requires OpenAI API key' },
              { id: 'anthropic', name: 'Anthropic (Claude)', free: false, desc: 'Requires Anthropic API key' },
              { id: 'google', name: 'Google Gemini', free: false, desc: 'Requires Google API key' },
            ].map((provider) => (
              <div
                key={provider.id}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {provider.name}
                    </span>
                    {provider.free && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                        Free
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {provider.desc}
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Configure
                </Button>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              How to get API keys:
            </h4>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li>• <strong>OpenAI:</strong> platform.openai.com/api-keys</li>
              <li>• <strong>Anthropic:</strong> console.anthropic.com/settings/keys</li>
              <li>• <strong>Google:</strong> makersuite.google.com/app/apikey</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* App Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-green-600" />
            <CardTitle>App Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Version</span>
              <p className="font-medium">1.0.0</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Build</span>
              <p className="font-medium">Cloudflare Pages</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Framework</span>
              <p className="font-medium">React + Vite</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Styling</span>
              <p className="font-medium">Tailwind CSS 4</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPage;